import React, { useContext, useState } from "react"
import styled from "@emotion/styled"
import Divider from "@mui/material/Divider"
import mainContext from "../../contexts/mainContext"
import {
  __constants,
  __applicationStatus,
  __applicationStatusDisplay,
  __snackBarSeverity,
  __snackBarText,
} from "../../constants"
import { getJobsAppliersController } from "../../Controllers/BookingManagement"
import Pagination from "@mui/material/Pagination"
import { Button, Tooltip } from "@mui/material"
import { useNavigate } from "react-router-dom"
import { formatWithTZ } from "../../utils"

const JobAppliersListing = () => {
  const url = new URLSearchParams(window.location.search)
  const { setProfileNavTitle, setSnackBar, setPageload } = useContext(mainContext)
  const [currentPage, setCurrentPage] = React.useState(1)
  const [data, setData] = useState([])
  const [metaData, setMetaData] = useState(null)
  const navigate = useNavigate()

  const statusClassMap = {
    [__applicationStatus.applied]: "status_pending",
    [__applicationStatus.cancelled]: "status_cancel",
    [__applicationStatus.declined]: "status_declined",
    [__applicationStatus.hired]: "status_processing",
    [__applicationStatus.completed]: "status_awaiting",
    [__applicationStatus.finalized]: "status_active",
    [__applicationStatus.expired]: "status_expired",
    [__applicationStatus.refundByNona]: "status_refunded",
    [__applicationStatus.partiallyRefundByNona]: "status_partially_refunded",

  }

  const callSnackBar = (msg, type) => {
    setSnackBar({
      status: true,
      text: msg,
      severity: __snackBarSeverity[type],
    })
  }

  const getJobsAppliers = async (page = 1) => {
    setPageload(true)
    const res = await getJobsAppliersController(url.get("id"), page)
    setPageload(false)
    if (res && res.status === 500) {
      return callSnackBar(__snackBarText.internalServerError, "error")
    }
    if (res.data.status !== 200) {
      return callSnackBar(res.data.errors[0].message, "error")
    }
    setData(res.data.data.allAppliers[0].data)
    setMetaData(res.data.data.allAppliers[0].metadata[0])
  }

  const handlePaginationClick = (e, page) => {
    setCurrentPage(page)
    getJobsAppliers(page)
  }

  React.useEffect(() => {
    getJobsAppliers()
    setProfileNavTitle("Applier's listing")
    // eslint-disable-next-line
  }, [])

  return (
    <>
      <Container>
        <Header>
          <BackBtnContainer>
            <BackBtn variant="outlined" size="small" onClick={() => navigate(-1)}>
              Back
            </BackBtn>
          </BackBtnContainer>
          <ListGrid>
            <li>Status</li>
            <li>Applier name</li>
            <li>Applier email</li>
            <li>Applier number</li>
            <li>Role</li>
            <li>Applied on</li>
            <li>Reason</li>
          </ListGrid>
        </Header>
        <Divider />
        <Body>
          {data.length === 0 ? (
            <NoRecord>
              <span>No Record Found</span>
            </NoRecord>
          ) : (
            data.map((e, index) => {
              // Determine who cancelled/declined
              const cancelledBy = e.reasonSeekerCancelled
                ? e.reasonSeekerCancelled
                : e.reasonProviderCancelled
                  ? e.reasonProviderCancelled
                  : e.reasonSeekerDeclined
                    ? e.reasonSeekerDeclined
                    : e.reasonProviderDeclined
                      ? e.reasonProviderDeclined
                      : ""

              const toolTipcancelledBy = e.reasonSeekerCancelled
                ? `Cancelled by Seeker - ${e.reasonSeekerCancelled}`
                : e.reasonProviderCancelled
                  ? `Cancelled by Provider - ${e.reasonProviderCancelled}`
                  : e.reasonSeekerDeclined
                    ? `Declined by Seeker - ${e.reasonSeekerDeclined}`
                    : e.reasonProviderDeclined
                      ? `Declined by Provider - ${e.reasonProviderDeclined}`
                      : ""

              return (
                <ListGrid key={index}>
                <li style={{ overflow: "hidden", display: "flex" }}>
                      <li className={`list_card_status ${statusClassMap[e.jobStatus] || "status_active"}`}>
                          {__applicationStatusDisplay[e.jobStatus] || "Unknown"}
                      </li>
                  </li>
                  <li>{e.userInformation?.firstName} {e.userInformation?.lastName}</li>
                  <li>{e.userInformation?.email}</li>
                  <li>{e.userInformation?.mobileNo}</li>
                  <li>{e.userInformation?.role}</li>
                  <li>{formatWithTZ(e?.createdAt, "Do MMM, YYYY")}</li>
                  <li>
                    {cancelledBy ? (
                      <Tooltip
                        title={toolTipcancelledBy}
                        placement="top"
                        enterDelay={300}
                        leaveDelay={200}
                        arrow
                        PopperProps={{
                          sx: {
                            "& .MuiTooltip-tooltip": {
                              maxWidth: "300px",
                              fontSize: "0.75rem",
                              padding: "8px 12px",
                              lineHeight: 1.5,
                            },
                          },
                        }}
                      >
                        <ReasonText>{cancelledBy}</ReasonText>
                      </Tooltip>
                    ) : (
                      <span>-</span>
                    )}
                  </li>
                </ListGrid>
              )
            })
          )}
        </Body>
      </Container>

      <PaginationContainer>
        <Pagination
          count={Math.ceil(metaData?.total / __constants.listingLimit) || 1}
          page={currentPage}
          color="primary"
          shape="rounded"
          onChange={(e, page) => handlePaginationClick(e, page)}
        />
      </PaginationContainer>
    </>
  )
}

const Container = styled("div")`
    padding-top: 1rem;
    font-size: 85%;
    min-height: 80vh;
`

const Header = styled("div")`
    margin: 0.5rem 0;
    ul li{
      font-family: "semi-bold" !important;
    }
`

const BackBtnContainer = styled("div")`
    margin-bottom: 1rem;
`

const Body = styled("div")`
    margin-top: 1rem;
    display: grid;
    row-gap: 1rem;
`

const ListGrid = styled("ul")`
    display: grid;
    column-gap: 0.5rem;
    grid-template-columns: 1.3fr 2.3fr 3fr 1.5fr 1.5fr 1.2fr 1.8fr;
    align-items: center;
    padding-left: 0;
    margin: 0;
    list-style: none;
    
    li {
        padding: 0.5rem 0.25rem;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`


const ReasonText = styled("span")`
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: pointer;
    max-width: 100%;
    &:hover {
        color: #1976d2;
        text-decoration: underline;
    }
`

const NoRecord = styled("div")`
    text-align: center;
    margin-top: 2rem;
    color: #777;
`

const BackBtn = styled(Button)`
    text-transform: none;
    font-size: 0.75rem;
    padding: 2px 10px;
`

const PaginationContainer = styled("div")`
    display: flex;
    justify-content: center;
    margin-top: 1.5rem;
    margin-bottom: 1rem;
`

export default JobAppliersListing